import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import FileList from './FileList';
import { api } from '../services/api';

const appLogo = new URL('../assets/bbyb-logo.png', import.meta.url);

/* ── Rich text: render **term** as highlighted bold ─────────────────────── */
const RichMessage = ({ text, className = '' }) => {
    if (!text) return null;
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return (
        <span className={className}>
            {parts.map((part, i) =>
                part.startsWith('**') && part.endsWith('**')
                    ? <strong key={i} className="font-semibold" style={{ color: '#660F09' }}>{part.slice(2, -2)}</strong>
                    : part
            )}
        </span>
    );
};

/* ── Time helper ─────────────────────────────────────────────────────────── */
const timeAgo = (date) => {
    const s = Math.floor((Date.now() - new Date(date)) / 1000);
    if (s < 60)    return 'just now';
    if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
    if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
    return `${Math.floor(s / 86400)}d ago`;
};

/* ── Notification icon ───────────────────────────────────────────────────── */
const NotifIcon = ({ type, large = false }) => {
    const configs = {
        success: { bg: 'bg-emerald-100', path: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', color: 'text-emerald-600' },
        error:   { bg: 'bg-red-100',     path: 'M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z', color: 'text-red-500' },
        warning: { bg: 'bg-amber-100',   path: 'M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z', color: 'text-amber-500' },
        info:    { bg: 'bg-blue-100',    path: 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z', color: 'text-blue-500' },
    };
    const { bg, path, color } = configs[type] ?? configs.info;
    const size = large ? 'w-10 h-10' : 'w-8 h-8';
    const iconSize = large ? 'w-5 h-5' : 'w-4 h-4';
    return (
        <div className={`${size} rounded-full flex items-center justify-center flex-shrink-0 ${bg}`}>
            <svg className={`${iconSize} ${color}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={path} />
            </svg>
        </div>
    );
};

/* ════════════════════════════════════════════════════════════════════════════
   Notification Board (slide-over drawer)
   ════════════════════════════════════════════════════════════════════════════ */
const NotificationBoard = ({ notifications, onClose, onMarkAllRead, onClear }) => {
    const unread = notifications.filter(n => !n.read).length;

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 z-[400] bg-black/30 backdrop-blur-sm"
                onClick={onClose}
            />
            {/* Slide-over panel */}
            <div className="fixed right-0 top-0 bottom-0 w-full max-w-sm bg-white z-[401] shadow-2xl flex flex-col">
                {/* Header */}
                <div className="px-5 pt-5 pb-4 text-white flex-shrink-0"
                    style={{ background: 'linear-gradient(to right, #660F09, #895129)' }}>
                    <div className="flex items-start justify-between">
                        <div>
                            <h2 className="text-base font-bold">Inbox</h2>
                            <p className="text-xs mt-0.5" style={{ color: '#AD7A32' }}>
                                {unread > 0 ? `${unread} unread` : 'All caught up'}
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    {notifications.length > 0 && (
                        <div className="flex items-center gap-2 mt-3">
                            {unread > 0 && (
                                <button
                                    onClick={onMarkAllRead}
                                    className="text-xs font-semibold px-3 py-1 bg-white/20 hover:bg-white/30 rounded-full transition"
                                >
                                    Mark all read
                                </button>
                            )}
                            <button
                                onClick={() => { onClear(); }}
                                className="text-xs font-semibold px-3 py-1 bg-white/20 hover:bg-white/30 rounded-full transition"
                            >
                                Clear all
                            </button>
                        </div>
                    )}
                </div>

                {/* List */}
                <div className="flex-1 overflow-y-auto divide-y divide-gray-50">
                    {notifications.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full py-16">
                            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                                <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                </svg>
                            </div>
                            <p className="text-sm font-semibold text-gray-500">No notifications</p>
                            <p className="text-xs text-gray-400 mt-1">You're all caught up!</p>
                        </div>
                    ) : (
                        notifications.map(n => (
                            <div
                                key={n.id}
                                className={`px-5 py-4 flex items-start gap-3 transition-colors ${n.read ? '' : 'bg-[#895129]/5'}`}
                            >
                                <NotifIcon type={n.type} large />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                        <p className="text-sm font-semibold text-gray-800 leading-tight">{n.title}</p>
                                        {!n.read && (
                                            <span className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: '#895129' }} />
                                        )}
                                    </div>
                                    {n.message && (
                                        <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                                            <RichMessage text={n.message} />
                                        </p>
                                    )}
                                    <p className="text-[10px] text-gray-300 mt-1.5">{timeAgo(n.created_at)}</p>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </div>
        </>
    );
};

/* ════════════════════════════════════════════════════════════════════════════
   MainLayout
   ════════════════════════════════════════════════════════════════════════════ */
const MainLayout = ({ token, user, onLogout, notifications = [], addNotification, markAllRead, clearNotifications, refreshNotifications, simplified = false }) => {
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [uploading, setUploading]           = useState(false);
    const [userMenuOpen, setUserMenuOpen]     = useState(false);
    const [bellOpen, setBellOpen]             = useState(false);
    const [boardOpen, setBoardOpen]           = useState(false);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const fileInputRef = useRef();
    const menuRef      = useRef();
    const bellRef      = useRef();
    const navigate     = useNavigate();

    const unreadCount = notifications.filter(n => !n.read).length;

    useEffect(() => {
        const handler = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setUserMenuOpen(false);
            if (bellRef.current && !bellRef.current.contains(e.target)) setBellOpen(false);
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const uploadFiles = async (files) => {
        const fileArr = Array.from(files);
        setUploading(true);

        for (const file of fileArr) {
            try {
                const fd = new FormData();
                fd.append('file', file);
                await api.uploadFile(fd);
                addNotification?.({ type: 'success', title: 'Upload complete', message: file.name });
            } catch {
                addNotification?.({ type: 'error', title: 'Upload failed', message: file.name });
            }
        }

        setUploading(false);
        setRefreshTrigger(p => p + 1);
    };

    const handleBellClick = () => {
        if (!bellOpen) {
            refreshNotifications?.();
            markAllRead?.();
        }
        setBellOpen(o => !o);
    };

    const openBoard = () => {
        setBellOpen(false);
        setBoardOpen(true);
    };

    return (
        <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#f9f4f0' }}>

            {/* ── Header ──────────────────────────────────────────────────── */}
            <header className="text-white shadow-lg" style={{ background: 'linear-gradient(to right, #660F09, #895129)' }}>
                <div className="max-w-7xl mx-auto px-3 py-3 sm:px-6 sm:py-4 flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                        <img src={appLogo.href} alt="" className="h-7 w-auto object-contain rounded hidden sm:block" />
                        <div className="min-w-0">
                            <Link to="/" className="text-sm sm:text-xl font-bold tracking-tight hover:opacity-90 truncate block">BrickByBrickVault</Link>
                            <p className="text-xs mt-0.5 hidden sm:block" style={{ color: '#AD7A32' }}>
                                Welcome back, <span className="font-semibold text-white">{user?.username}</span>
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {/* Bell */}
                        <div className="relative" ref={bellRef}>
                            <button
                                onClick={handleBellClick}
                                className="relative flex items-center justify-center w-9 h-9 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg transition"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                </svg>
                                {unreadCount > 0 && (
                                    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
                                        {unreadCount > 9 ? '9+' : unreadCount}
                                    </span>
                                )}
                            </button>

                            {bellOpen && (
                                <div className="absolute right-0 mt-2 w-[calc(100vw-2rem)] sm:w-80 max-w-sm bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden">
                                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
                                        <div className="flex items-center gap-2">
                                            <span className="text-sm font-bold text-gray-800">Notifications</span>
                                            {unreadCount > 0 && (
                                                <span className="text-[10px] font-bold bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full">
                                                    {unreadCount} new
                                                </span>
                                            )}
                                        </div>
                                        {notifications.length > 0 && (
                                            <button onClick={clearNotifications} className="text-xs text-gray-400 hover:text-gray-600 transition">
                                                Clear all
                                            </button>
                                        )}
                                    </div>
                                    <div className="max-h-72 overflow-y-auto divide-y divide-gray-50">
                                        {notifications.length === 0 ? (
                                            <div className="px-4 py-8 text-center">
                                                <svg className="w-8 h-8 text-gray-200 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                                </svg>
                                                <p className="text-sm text-gray-400">No notifications</p>
                                            </div>
                                        ) : (
                                            notifications.slice(0, 5).map(n => (
                                                <button
                                                    key={n.id}
                                                    onClick={openBoard}
                                                    className={`w-full text-left px-4 py-3 flex items-start gap-3 transition hover:bg-gray-50 ${n.read ? '' : 'bg-[#895129]/5'}`}
                                                >
                                                    <NotifIcon type={n.type} />
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-semibold text-gray-800">{n.title}</p>
                                                        <p className="text-xs text-gray-500 mt-0.5 truncate"><RichMessage text={n.message} /></p>
                                                        <p className="text-[10px] text-gray-300 mt-0.5">{timeAgo(n.created_at)}</p>
                                                    </div>
                                                    {!n.read && <span className="w-2 h-2 rounded-full flex-shrink-0 mt-1.5" style={{ backgroundColor: '#895129' }} />}
                                                </button>
                                            ))
                                        )}
                                    </div>
                                    {/* View all button */}
                                    <div className="border-t border-gray-100 p-2">
                                        <button
                                            onClick={openBoard}
                                            className="w-full flex items-center justify-center gap-1.5 text-xs font-semibold py-2 rounded-lg hover:bg-gray-50 transition"
                                            style={{ color: '#895129' }}
                                        >
                                            View all notifications
                                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* User menu */}
                        <div className="relative" ref={menuRef}>
                            <button onClick={() => setUserMenuOpen(o => !o)}
                                className="flex items-center gap-2 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg px-3 py-2 transition">
                                <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center font-bold text-sm" style={{ color: '#895129' }}>
                                    {user?.username?.[0]?.toUpperCase() ?? 'U'}
                                </div>
                                <span className="text-sm font-medium hidden sm:block">{user?.username}</span>
                                <svg className={`w-4 h-4 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>

                            {userMenuOpen && (
                                <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-1 z-50">
                                    <div className="px-4 py-2.5 border-b border-gray-100">
                                        <p className="text-xs font-semibold text-gray-500 uppercase">Signed in as</p>
                                        <p className="text-sm text-gray-800 font-medium truncate">{user?.email}</p>
                                        <span className="inline-block mt-1 text-[10px] px-1.5 py-0.5 rounded font-semibold uppercase"
                                            style={{ backgroundColor: 'rgba(137,81,41,0.12)', color: '#895129' }}>
                                            {user?.role}
                                        </span>
                                    </div>
                                    <Link to="/settings" onClick={() => setUserMenuOpen(false)}
                                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50">
                                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        Settings
                                    </Link>
                                    {user?.role === 'admin' && (
                                        <Link to="/admin" onClick={() => setUserMenuOpen(false)}
                                            className="flex items-center gap-2 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50">
                                            <svg className="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                            </svg>
                                            Admin Console
                                        </Link>
                                    )}
                                    <div className="border-t border-gray-100 mt-1">
                                        <button onClick={() => { setUserMenuOpen(false); setShowLogoutConfirm(true); }}
                                            className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                            </svg>
                                            Sign out
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </header>

            {/* ── Main content ─────────────────────────────────────────────── */}
            <main className="flex-1 max-w-7xl w-full mx-auto px-3 py-4 sm:px-6 sm:py-6">
                <FileList token={token} refreshTrigger={refreshTrigger} addNotification={addNotification} simplified={simplified} />
            </main>

            {/* ── Floating upload button ────────────────────────────────────── */}
            <input ref={fileInputRef} type="file" multiple className="hidden" onChange={e => uploadFiles(e.target.files)} />
            <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                title="Upload files"
                className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8 z-[150] flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full shadow-2xl transition-all disabled:opacity-80 hover:scale-105 active:scale-95"
                style={{ background: 'linear-gradient(135deg, #660F09, #895129)' }}
            >
                {uploading
                    ? <div className="w-5 h-5 sm:w-6 sm:h-6 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                    : <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                    </svg>
                }
            </button>

            {/* ── Notification board (slide-over) ──────────────────────────── */}
            {boardOpen && (
                <NotificationBoard
                    notifications={notifications}
                    onClose={() => setBoardOpen(false)}
                    onMarkAllRead={() => { markAllRead?.(); }}
                    onClear={() => { clearNotifications?.(); setBoardOpen(false); }}
                />
            )}

            {/* Sign out confirmation */}
            {showLogoutConfirm && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setShowLogoutConfirm(false)} />
                    <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-sm p-6">
                        <h3 className="text-sm font-bold text-slate-800 mb-3">Sign out?</h3>
                        <div className="text-sm text-slate-500 mb-5">Are you sure you want to sign out?</div>
                        <div className="flex items-center gap-3 justify-end">
                            <button onClick={() => setShowLogoutConfirm(false)} className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg transition">
                                Cancel
                            </button>
                            <button onClick={() => { setShowLogoutConfirm(false); onLogout(); }} className="px-4 py-2 text-sm font-bold text-white bg-red-600 hover:bg-red-700 rounded-lg transition">
                                Sign out
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MainLayout;
